import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthStore } from '@scl/core/domains/auth/services/auth.store';
import { AccountService } from '../../core-module/services/account.service';
import { UserProfileService } from '../../core-module/services/user-profile.service';
import { AppAccessToken } from '../models/app-access-token';
import { AuthService } from '../services/auth.service';
import { JwtTokenService } from '../services/jwt-token.service';
import { LocalStorageService } from '../services/local-storage.service';
import { Constants } from '../utils/constants';
import { RoutingService } from '../services/routing.service';
import { environment } from '@environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly authStore = inject(AuthStore);

  constructor(
    private localStorageService: LocalStorageService,
    private jwtTokenService: JwtTokenService,
    private authService: AuthService,
    private accountService: AccountService,
    private userProfileService: UserProfileService,
    private routingService: RoutingService
  ) { }
  // TODO: Check if this works for other modules rather than AppModule.
  intercept(req: HttpRequest<any>, next: HttpHandler) {
    if (!this.isApiRequest(req.url) || this.isProviderRequest(req.url)) {
      return next.handle(req);
    }

    if (req.headers.has(Constants.InterceptorSkipHeader)) {
      let headers = req.headers.delete(Constants.InterceptorSkipHeader);
      return next.handle(req.clone({ headers }));
    }

    // When the provider is Clerk, the clerkAuthInterceptor (functional, runs first) handles
    // token injection. This legacy interceptor must not interfere.
    // Also skip when the provider config hasn't loaded yet — the clerkAuthInterceptor
    // awaits ensureProviderConfig() internally, so it will handle the request correctly.
    // Without this check, isClerk() returns false before the config loads and the interceptor
    // would incorrectly redirect the user to the IdentityServer OIDC flow.
    if (this.authStore.isClerk() || !this.authStore.isProviderReady()) {
      return next.handle(req);
    }
    
    let token = this.jwtTokenService.accessToken;
    if (!token?.access_token) {
      token = this.localStorageService.getToken();
    }
    
    if (!token?.access_token) {
      this.accountService.currentUser = null;
      this.userProfileService.removeUserProfile();
      this.authService.clearToken();
      this.authService.initCodeAuthorizationPkce();
      return next.handle(req);
    }

    req = this.addToken(req, token.access_token!);
    return next.handle(req).pipe(catchError(error => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return this.handle401Error(req, next, token?.refresh_token!);
      } else {
        return throwError(error);
      }
    }));
  }

  private addToken(request: HttpRequest<any>, token: string) {
    if (request.headers.has(Constants.InterceptorFileHeader)) {
      let headers = request.headers;
      headers = headers.append('Authorization', `Bearer ${token}`);
      return request.clone({ headers });
    }

    return request.clone({
      setHeaders: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
  }

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private handle401Error(request: HttpRequest<any>, next: HttpHandler, refreshToken: string) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      
      return this.authService.acquireTokenByRefreshToken(refreshToken).pipe(
        switchMap((newToken: AppAccessToken) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken.access_token);          
          this.jwtTokenService.accessToken = newToken;
          return next.handle(this.addToken(request, newToken.access_token!));
        }),
        catchError(error => {
          this.accountService.currentUser = null;
          this.userProfileService.removeUserProfile();
          this.authService.clearToken();
          if (!this.authStore.isClerk()) {
            this.authService.initCodeAuthorizationPkce();
          }
          return throwError(error);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(access_token => {
          return next.handle(this.addToken(request, access_token));
        }),
        catchError(error => {
          this.accountService.currentUser = null;
          this.userProfileService.removeUserProfile();
          this.authService.clearToken();
          if (!this.authStore.isClerk()) {
            this.authService.initCodeAuthorizationPkce();
          }
          return throwError(error);
        })
      );
    }
  }

  private isApiRequest(url: string): boolean {
    if ((environment.apiUrlBase 
        && url.startsWith(environment.apiUrlBase))
        || (environment.authServerUrlBase
        && url.startsWith(environment.authServerUrlBase))) {
      return true;
    }

    return url.startsWith('/api/') || url.startsWith('api/');
  }

  private isProviderRequest(url: string): boolean {
    const normalizedUrl = environment.apiUrlBase && url.startsWith(environment.apiUrlBase)
        ? url.slice(environment.apiUrlBase.length)
        : url;

    return normalizedUrl === '/api/auth/provider'
      || normalizedUrl === 'api/auth/provider';
  }
}
